/**
 * Ambiente da Região 2 — SHATTERED WILDS (Terras Fraturadas). Bioma próprio do VOID HUNTER:
 * pedra clara azulada, cristais grandes (InstancedMesh), formações rochosas, grama baixa,
 * arbustos, ruínas antigas e fendas de energia. Densidade inspirada nas referências, sem
 * poluir. Tudo compartilha geometria/material (mobile-first) e vive num Group que o
 * RegionManager mostra/esconde.
 */
import * as THREE from "three";
import { type VisualTheme } from "../config/GameConfig";
import { regionHalfSize } from "./Territories";
import { randRange } from "../utils/math";

export class ShatteredWilds {
  readonly group = new THREE.Group();
  private readonly half = regionHalfSize("shatteredWilds"); // 10.6H
  /** 10.6J — os marcos eram contados a olho para um mapa de 210 m. A área cresceu e eles
   *  ficaram esparsos; agora a quantidade acompanha a ÁREA (proporção ao quadrado). */
  private readonly dens = Math.max(1, (this.half / 105) ** 2);
  private readonly crystalMats: THREE.MeshStandardMaterial[] = [];
  private pulse = 0;
  private readonly fissureMats: THREE.MeshBasicMaterial[] = [];

  constructor(
    scene: THREE.Scene,
    private readonly theme: VisualTheme,
  ) {
    this.buildGround();
    this.buildPaths();
    this.buildFissures();
    this.buildCrystals();
    this.buildRocks();
    this.buildFoliage();
    this.buildRuins();
    this.buildBoundary();
    this.group.visible = false;
    scene.add(this.group);
  }

  /** 10.5S — o CHÃO agora é do Arena (bioma por território + colisão). Aqui ficou só
   *  o que é marco desta região; sem chão/grid próprios para não brigar com ele. */
  private buildGround(): void {
    /* intencionalmente vazio */
  }

  private buildPaths(): void {
    // caminhos naturais claros (faixas de pedra clara ligando pontos)
    const mat = new THREE.MeshStandardMaterial({ color: 0x3f5f74, roughness: 1, transparent: true, opacity: 0.6, depthWrite: false });
    const geo = new THREE.PlaneGeometry(3, 10);
    let z = 20;
    for (let i = 0; i < Math.round(7 * this.dens); i++) {
      const seg = new THREE.Mesh(geo, mat);
      seg.rotation.x = -Math.PI / 2;
      seg.position.set(randRange(-3, 3), 0.04, z);
      seg.rotation.z = randRange(-0.3, 0.3);
      this.group.add(seg);
      z -= 11;
    }
  }

  private buildFissures(): void {
    // fendas de energia no chão (cracks emissivos)
    const geo = new THREE.PlaneGeometry(0.6, 5);
    for (let i = 0; i < Math.round(10 * this.dens); i++) {
      const mat = new THREE.MeshBasicMaterial({ color: this.theme.accent, transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending });
      this.fissureMats.push(mat);
      const f = new THREE.Mesh(geo, mat);
      f.rotation.x = -Math.PI / 2;
      f.position.set(randRange(-this.half + 6, this.half - 6), 0.05, randRange(-this.half + 6, this.half - 6));
      f.rotation.z = randRange(0, Math.PI);
      f.scale.set(randRange(0.6, 1.6), randRange(0.6, 2.2), 1);
      this.group.add(f);
    }
  }

  private buildCrystals(): void {
    // cristais grandes com VARIAÇÃO (escala/rotação/altura/grupo) via InstancedMesh
    const geo = new THREE.OctahedronGeometry(1, 0);
    const count = 90;
    const colors = [this.theme.accent, 0x7cf0ff, 0x8ea2ff];
    // 3 InstancedMesh (uma por cor) para variar sem material por instância
    for (let ci = 0; ci < colors.length; ci++) {
      const mat = new THREE.MeshStandardMaterial({ color: colors[ci], emissive: colors[ci], emissiveIntensity: 0.7, roughness: 0.3, metalness: 0.2, flatShading: true });
      this.crystalMats.push(mat);
      const inst = new THREE.InstancedMesh(geo, mat, count);
      const m = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const e = new THREE.Euler();
      let n = 0;
      // clusters: alguns cristais nascem em grupos
      while (n < count) {
        const cx = randRange(-this.half + 5, this.half - 5);
        const cz = randRange(-this.half + 5, this.half - 5);
        if (Math.hypot(cx, cz) < 11) continue; // longe do sanctuary
        const clusterSize = 1 + Math.floor(Math.random() * 3);
        for (let k = 0; k < clusterSize && n < count; k++) {
          const s = randRange(0.5, 2.6);
          const px = cx + randRange(-1.5, 1.5);
          const pz = cz + randRange(-1.5, 1.5);
          e.set(randRange(-0.2, 0.2), randRange(0, Math.PI * 2), randRange(-0.2, 0.2));
          q.setFromEuler(e);
          m.compose(new THREE.Vector3(px, s * 0.8, pz), q, new THREE.Vector3(s * 0.5, s, s * 0.5));
          inst.setMatrixAt(n, m);
          n++;
        }
      }
      inst.instanceMatrix.needsUpdate = true;
      inst.castShadow = true;
      this.group.add(inst);
    }
  }

  private buildRocks(): void {
    const geo = new THREE.IcosahedronGeometry(1, 0);
    const mat = new THREE.MeshStandardMaterial({ color: this.theme.stone, roughness: 1, flatShading: true });
    const inst = new THREE.InstancedMesh(geo, mat, 60);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    for (let i = 0; i < Math.round(60 * this.dens); i++) {
      let x = 0, z = 0;
      for (let t = 0; t < 8; t++) { x = randRange(-this.half + 4, this.half - 4); z = randRange(-this.half + 4, this.half - 4); if (Math.hypot(x, z) > 10) break; }
      const s = randRange(0.5, 2.2);
      e.set(randRange(0, 3), randRange(0, 3), randRange(0, 3));
      q.setFromEuler(e);
      m.compose(new THREE.Vector3(x, s * 0.3, z), q, new THREE.Vector3(s, s * randRange(0.6, 1), s));
      inst.setMatrixAt(i, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.castShadow = true;
    inst.receiveShadow = true;
    this.group.add(inst);
  }

  private buildFoliage(): void {
    // grama baixa + arbustos resistentes (verde frio)
    const grassGeo = new THREE.ConeGeometry(0.18, 0.6, 4);
    const grassMat = new THREE.MeshStandardMaterial({ color: 0x2f6b52, roughness: 0.9, flatShading: true });
    const grass = new THREE.InstancedMesh(grassGeo, grassMat, 160);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    for (let i = 0; i < Math.round(160 * this.dens); i++) {
      const x = randRange(-this.half + 3, this.half - 3);
      const z = randRange(-this.half + 3, this.half - 3);
      const s = randRange(0.6, 1.4);
      q.setFromEuler(new THREE.Euler(0, randRange(0, 6), 0));
      m.compose(new THREE.Vector3(x, s * 0.3, z), q, new THREE.Vector3(s, s, s));
      grass.setMatrixAt(i, m);
    }
    grass.instanceMatrix.needsUpdate = true;
    this.group.add(grass);

    const bushGeo = new THREE.IcosahedronGeometry(0.6, 0);
    const bushMat = new THREE.MeshStandardMaterial({ color: 0x2a5a48, roughness: 0.95, flatShading: true });
    const bush = new THREE.InstancedMesh(bushGeo, bushMat, 30);
    for (let i = 0; i < Math.round(30 * this.dens); i++) {
      let x = 0, z = 0;
      for (let t = 0; t < 6; t++) { x = randRange(-this.half + 4, this.half - 4); z = randRange(-this.half + 4, this.half - 4); if (Math.hypot(x, z) > 11) break; }
      const s = randRange(0.7, 1.6);
      q.setFromEuler(new THREE.Euler(0, randRange(0, 6), 0));
      m.compose(new THREE.Vector3(x, s * 0.5, z), q, new THREE.Vector3(s, s * 0.8, s));
      bush.setMatrixAt(i, m);
    }
    bush.instanceMatrix.needsUpdate = true;
    bush.castShadow = true;
    this.group.add(bush);
  }

  private buildRuins(): void {
    // ruínas antigas: pilares quebrados + arcos de pedra
    const mat = new THREE.MeshStandardMaterial({ color: this.theme.stone, roughness: 1, flatShading: true });
    const pillarGeo = new THREE.CylinderGeometry(0.5, 0.6, 4, 6);
    for (let i = 0; i < Math.round(10 * this.dens); i++) {
      let x = 0, z = 0;
      for (let t = 0; t < 6; t++) { x = randRange(-this.half + 6, this.half - 6); z = randRange(-this.half + 6, this.half - 6); if (Math.hypot(x, z) > 13) break; }
      const h = randRange(1.2, 4);
      const p = new THREE.Mesh(pillarGeo, mat);
      p.scale.y = h / 4;
      p.position.set(x, h / 2, z);
      p.rotation.set(randRange(-0.1, 0.1), randRange(0, 6), randRange(-0.1, 0.1));
      p.castShadow = true;
      this.group.add(p);
    }
    // um arco central de ruína (ponto de interesse)
    const arch = new THREE.Group();
    const legGeo = new THREE.BoxGeometry(0.8, 4, 0.8);
    const l1 = new THREE.Mesh(legGeo, mat); l1.position.set(-2, 2, 0); l1.castShadow = true;
    const l2 = new THREE.Mesh(legGeo, mat); l2.position.set(2, 2, 0); l2.castShadow = true;
    const top = new THREE.Mesh(new THREE.BoxGeometry(5, 0.8, 0.8), mat); top.position.set(0, 4, 0); top.castShadow = true;
    arch.add(l1, l2, top);
    arch.position.set(-18, 0, -16);
    arch.rotation.y = 0.5;
    this.group.add(arch);
  }

  private buildBoundary(): void {
    const geo = new THREE.DodecahedronGeometry(1.6, 0);
    const mat = new THREE.MeshStandardMaterial({ color: 0x2b3a4a, roughness: 1, flatShading: true });
    const inst = new THREE.InstancedMesh(geo, mat, 64);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const r = this.half + 1.5;
    for (let i = 0; i < Math.round(64 * this.dens); i++) {
      const a = (i / 64) * Math.PI * 2;
      const s = randRange(1, 2.4);
      q.setFromEuler(new THREE.Euler(randRange(0, 3), randRange(0, 3), randRange(0, 3)));
      m.compose(new THREE.Vector3(Math.cos(a) * r + randRange(-1, 1), randRange(-0.6, 1.4), Math.sin(a) * r + randRange(-1, 1)), q, new THREE.Vector3(s, s, s));
      inst.setMatrixAt(i, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.castShadow = true;
    this.group.add(inst);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  update(dt: number): void {
    if (!this.group.visible) return;
    this.pulse += dt;
    const e = 0.55 + Math.sin(this.pulse * 1.3) * 0.25;
    for (const m of this.crystalMats) m.emissiveIntensity = e;
    const fo = 0.4 + Math.sin(this.pulse * 1.8) * 0.2;
    for (const m of this.fissureMats) m.opacity = fo;
  }
}
